// URL-stateful filter store. Every map view is shareable; state lives in
// the query string so back/forward, deep links, and copied URLs restore the
// view exactly.
//
// Range is encoded at DAY precision (d0/d1 are YYYY-MM-DD strings) so users
// can pinpoint a specific window — the "last 7 days" preset actually means
// 7 days, not 1 month. Backward-compat: legacy m0/m1 (YYYY-MM) and y0/y1
// (YYYY) params still load correctly.
import { useEffect, useState, useCallback } from 'react';
import type { FilterState } from './outbreaks';

export const MIN_YEAR = 1993;
export const MAX_YEAR = new Date().getUTCFullYear();
export const MONTHS_PER_YEAR = 12;
export const MIN_MONTH = 0;                       // Jan 1993, kept for histogram
export const MAX_MONTH =
  (MAX_YEAR - MIN_YEAR + 1) * MONTHS_PER_YEAR - 1; // Dec of current year
export const MONTH_NAMES = [
  'Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec',
] as const;

// Day precision — origin is 1993-01-01.
const ORIGIN_MS = Date.UTC(MIN_YEAR, 0, 1);
const DAY_MS = 86_400_000;
export const MIN_DAY = 0;
export const MAX_DAY = Math.floor((Date.now() - ORIGIN_MS) / DAY_MS); // today

export type ViewMode = 'markers' | 'heatmap' | 'choropleth';

export interface MapUrlState {
  filters: FilterState;
  selectedId: string | null;
  viewMode: ViewMode;
}

// ----- date <-> day-index helpers -----------------------------------------

/** "YYYY-MM-DD" or "YYYY-MM" → day-index (days since 1993-01-01). */
export function dayIndexFromDate(d: string): number {
  const [yStr, mStr, dStr] = d.split('-');
  const y = Number(yStr);
  const m = Number(mStr ?? '1');
  const dd = Number(dStr ?? '1');
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(dd)) return 0;
  const ms = Date.UTC(y, m - 1, dd);
  return clampDay(Math.floor((ms - ORIGIN_MS) / DAY_MS));
}

export function dayIndexToDate(i: number): { year: number; month: number; day: number } {
  const ms = ORIGIN_MS + i * DAY_MS;
  const d = new Date(ms);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth(), day: d.getUTCDate() };
}

export function dayIndexToYMD(i: number): string {
  const { year, month, day } = dayIndexToDate(i);
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function dayIndexToShort(i: number): string {
  const { year, month, day } = dayIndexToDate(i);
  return `${MONTH_NAMES[month]} ${day}, ${year}`;
}

export function dayIndexToShortNoYear(i: number): string {
  const { month, day } = dayIndexToDate(i);
  return `${MONTH_NAMES[month]} ${day}`;
}

/** Day-index of the first day of the month containing this day. Useful for
 *  histogram bucket boundary checks. */
export function dayToMonthIndex(i: number): number {
  const { year, month } = dayIndexToDate(i);
  return (year - MIN_YEAR) * MONTHS_PER_YEAR + month;
}

export function clampDay(i: number): number {
  return Math.max(MIN_DAY, Math.min(MAX_DAY, i));
}

// ----- preset ranges ------------------------------------------------------

export type RangePresetKey = 'all' | '1y' | '6m' | '3m' | '1m' | '7d';

export interface RangePreset {
  key: RangePresetKey;
  label: string;
}

export const RANGE_PRESETS: RangePreset[] = [
  { key: 'all', label: 'All time' },
  { key: '1y',  label: '1 year' },
  { key: '6m',  label: '6 months' },
  { key: '3m',  label: '3 months' },
  { key: '1m',  label: '1 month' },
  { key: '7d',  label: '7 days' },
];

export function presetToRange(key: RangePresetKey): [number, number] {
  switch (key) {
    case 'all': return [MIN_DAY, MAX_DAY];
    case '1y':  return [Math.max(MIN_DAY, MAX_DAY - 365), MAX_DAY];
    case '6m':  return [Math.max(MIN_DAY, MAX_DAY - 182), MAX_DAY];
    case '3m':  return [Math.max(MIN_DAY, MAX_DAY - 91),  MAX_DAY];
    case '1m':  return [Math.max(MIN_DAY, MAX_DAY - 30),  MAX_DAY];
    case '7d':  return [Math.max(MIN_DAY, MAX_DAY - 7),   MAX_DAY];
  }
}

/** Returns the preset key whose range exactly matches, or null. */
export function matchPreset([from, to]: [number, number]): RangePresetKey | null {
  for (const p of RANGE_PRESETS) {
    const [pf, pt] = presetToRange(p.key);
    if (Math.abs(from - pf) <= 1 && Math.abs(to - pt) <= 1) return p.key;
  }
  return null;
}

// ----- url <-> state ------------------------------------------------------

function parseSet(raw: string | null): Set<string> {
  if (!raw) return new Set();
  return new Set(raw.split(',').filter(Boolean));
}

function setToParam(s: Set<string>): string | null {
  if (!s.size) return null;
  return Array.from(s).sort().join(',');
}

export function readUrlState(): MapUrlState {
  const sp = new URLSearchParams(window.location.search);

  // Preferred: d0/d1 (YYYY-MM-DD). Fallbacks: m0/m1 (YYYY-MM), y0/y1 (YYYY).
  let from = MIN_DAY;
  let to = MAX_DAY;
  if (sp.has('d0')) from = dayIndexFromDate(sp.get('d0')!);
  else if (sp.has('m0')) from = dayIndexFromDate(`${sp.get('m0')}-01`);
  else if (sp.has('y0')) from = dayIndexFromDate(`${sp.get('y0')}-01-01`);
  if (sp.has('d1')) to = dayIndexFromDate(sp.get('d1')!);
  else if (sp.has('m1')) {
    const [y, m] = sp.get('m1')!.split('-').map(Number);
    // last day of the month
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    to = dayIndexFromDate(`${sp.get('m1')}-${String(last).padStart(2, '0')}`);
  } else if (sp.has('y1')) to = dayIndexFromDate(`${sp.get('y1')}-12-31`);

  const v = sp.get('v');
  const viewMode: ViewMode =
    v === 'heatmap' ? 'heatmap'
    : v === 'choropleth' ? 'choropleth'
    : 'markers';

  return {
    filters: {
      dayRange: [clampDay(from), clampDay(to)],
      pathogens: parseSet(sp.get('p')),
      syndromes: parseSet(sp.get('s')),
      statuses: parseSet(sp.get('st')),
      confidences: parseSet(sp.get('c')),
    },
    selectedId: sp.get('id'),
    viewMode,
  };
}

export function writeUrlState(state: MapUrlState): void {
  const sp = new URLSearchParams();
  const { filters, selectedId, viewMode } = state;
  if (filters.dayRange[0] !== MIN_DAY) sp.set('d0', dayIndexToYMD(filters.dayRange[0]));
  if (filters.dayRange[1] !== MAX_DAY) sp.set('d1', dayIndexToYMD(filters.dayRange[1]));
  const p  = setToParam(filters.pathogens);   if (p)  sp.set('p',  p);
  const s  = setToParam(filters.syndromes);   if (s)  sp.set('s',  s);
  const st = setToParam(filters.statuses);    if (st) sp.set('st', st);
  const c  = setToParam(filters.confidences); if (c)  sp.set('c',  c);
  if (selectedId) sp.set('id', selectedId);
  if (viewMode !== 'markers') sp.set('v', viewMode);
  const qs = sp.toString();
  const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState(null, '', url);
}

export function useUrlState(): [MapUrlState, (updater: (prev: MapUrlState) => MapUrlState) => void] {
  const [state, setState] = useState<MapUrlState>(() => readUrlState());

  useEffect(() => { writeUrlState(state); }, [state]);

  useEffect(() => {
    const onPop = () => setState(readUrlState());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const update = useCallback(
    (updater: (prev: MapUrlState) => MapUrlState) => setState(prev => updater(prev)),
    [],
  );

  return [state, update];
}

/**
 * Read one-shot camera parameters (lat / lng / zoom) from the URL,
 * falling back to a sensible per-user default derived from the
 * browser's timezone. Used by landing pages to deep-link a focused
 * view — e.g. /where/dubai redirects to /?lat=25.2&lng=55.27&zoom=4.5.
 *
 * No URL params + Europe timezone → opens centred on Europe.
 * No URL params + Americas timezone → opens centred on North America.
 * etc. Falls back to Europe when no signal is available, which is the
 * "least surprising" default for hantavirus surveillance (mature HFRS
 * surveillance + active MV Hondius monitoring sit there).
 */
export function readInitialCamera():
  | { lat: number; lng: number; zoom: number }
  | null {
  const sp = new URLSearchParams(window.location.search);
  const hasLat = sp.has('lat');
  const hasLng = sp.has('lng');
  if (hasLat && hasLng) {
    const lat = Number(sp.get('lat'));
    const lng = Number(sp.get('lng'));
    const zoomRaw = sp.get('zoom');
    const zoom = zoomRaw != null ? Number(zoomRaw) : 4;
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) {
      return null;
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    if (zoom < 0 || zoom > 22) return null;
    return { lat, lng, zoom };
  }
  // No explicit params — pick a default centred on the user's region.
  return defaultCameraForBrowser();
}

/**
 * Synchronous, permission-free guess at where the user is on the
 * planet, derived from `Intl.DateTimeFormat().resolvedOptions().timeZone`.
 * The timezone is set by the OS / browser locale and reliably correlates
 * with continent. Returns coordinates for a region centroid + a zoom
 * level that frames the continent comfortably on a globe projection.
 */
function defaultCameraForBrowser(): { lat: number; lng: number; zoom: number } {
  let tz = '';
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* ignore */ }
  if (tz.startsWith('America/')) {
    // North America centroid (skewed slightly north so most of US/Canada is in frame)
    return { lat: 40, lng: -95, zoom: 2.6 };
  }
  if (tz.startsWith('Asia/')) {
    return { lat: 30, lng: 100, zoom: 2.4 };
  }
  if (tz.startsWith('Africa/')) {
    return { lat: 4, lng: 21, zoom: 2.4 };
  }
  if (tz.startsWith('Australia/') || tz === 'Pacific/Auckland' || tz.startsWith('Pacific/')) {
    return { lat: -25, lng: 135, zoom: 2.8 };
  }
  if (tz.startsWith('Atlantic/')) {
    // Atlantic islands tend to want Europe in frame
    return { lat: 48, lng: 10, zoom: 3.7 };
  }
  // Europe, Antarctica, Indian/Etc/UTC, or unknown → Europe-centred.
  return { lat: 48, lng: 10, zoom: 3.7 };
}

/**
 * Returns true when the SPA has been mounted inside an iframe on a
 * landing page (e.g. /where/argentina/, /event/2026-mv-hondius). The
 * embed=1 URL param suppresses chrome that's redundant in an embedded
 * context — the legal gate (already shown on the parent page footer),
 * the onboarding tour (parent has its own copy), and the brand watermark
 * (parent has its own header). Sidebar + map + filters + timeline all
 * stay because that's the actual product the embed is for.
 */
export function isEmbedMode(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const sp = new URLSearchParams(window.location.search);
    return sp.get('embed') === '1';
  } catch {
    return false;
  }
}

export function emptyFilters(): FilterState {
  return {
    dayRange: [MIN_DAY, MAX_DAY],
    pathogens: new Set(),
    syndromes: new Set(),
    statuses: new Set(),
    confidences: new Set(),
  };
}

export function filtersAreDefault(f: FilterState): boolean {
  return (
    f.dayRange[0] === MIN_DAY &&
    f.dayRange[1] === MAX_DAY &&
    f.pathogens.size === 0 &&
    f.syndromes.size === 0 &&
    f.statuses.size === 0 &&
    f.confidences.size === 0
  );
}
